import type { Icon as PhosphorIcon } from '@phosphor-icons/react'
import { EnvelopeIcon, ImageSquareIcon, LinkIcon, PhoneIcon } from '@phosphor-icons/react'
import { TFunction } from 'i18next'
import z from 'zod'

import type { SelectOption } from '@/components/ui/select/select.types'
import { EMAIL_REGEX } from '@/constants/regex'

import type { EssentialIcon, EssentialType } from './community-essentials.types'

export const ESSENTIAL_ICON_MAP: Record<EssentialIcon, PhosphorIcon> = {
  email: EnvelopeIcon,
  file: ImageSquareIcon,
  link: LinkIcon,
  phone: PhoneIcon,
}

export const INPUT_TYPE_OPTIONS: SelectOption<EssentialType>[] = [
  { label: 'Link', value: 'link' },
  { label: 'Phone number', value: 'number' },
  { label: 'Email', value: 'email' },
  { label: 'Texting', value: 'texting' },
  { label: 'Image', value: 'image' },
  { label: 'PDF', value: 'pdf' },
]

export const ICON_OPTIONS: SelectOption<EssentialIcon>[] = [
  { icon: LinkIcon, label: 'Link', value: 'link' },
  { icon: PhoneIcon, label: 'Phone number', value: 'phone' },
  { icon: EnvelopeIcon, label: 'Email', value: 'email' },
  { icon: ImageSquareIcon, label: 'Image/PDF', value: 'file' },
]

// TODO: must be a part of global logic, replace when available
export const ADMIN_ROLES = ['Administrator', 'PropertyManager']

export const EXTERNAL_LINK_TYPES: EssentialType[] = ['link', 'pdf', 'image']

export const MIN_PATH_LENGTH_FOR_PARENT = 2

export const START_POSITION = 1

const MAX_FILE_SIZE_MB = 10

export const MAX_FILE_SIZE = MAX_FILE_SIZE_MB * 1024 * 1024

export const getFileSizeErrorMessage = (t: TFunction<['community-essentials']>) =>
  t('community-essentials:fileSizeError', { maxSizeMb: MAX_FILE_SIZE_MB })

export const MAX_FOLDER_DEPTH = 5

export const makeFolderFormSchema = (t: TFunction<['community-essentials']>) =>
  z.object({
    name: z.string().min(1, t('community-essentials:folderFormModal.name.validation.required')),
    targetFolderId: z.number(),
  })

export type FolderFormData = z.infer<ReturnType<typeof makeFolderFormSchema>>

export const makeEssentialFormSchema = (t: TFunction<['community-essentials']>) =>
  z
    .object({
      folderId: z.number(),
      icon: z.string().min(1, t('community-essentials:required')),
      name: z.string().min(1, t('community-essentials:required')),
      type: z.string().min(1, t('community-essentials:required')),
      value: z.string(),
    })
    .superRefine((data, ctx) => {
      if (['link', 'email', 'texting', 'number'].includes(data.type)) {
        if (!data.value) {
          ctx.addIssue({
            code: 'custom',
            message: t('community-essentials:required'),
            path: ['value'],
          })
          return
        }
        if (data.type === 'link') {
          try {
            new URL(data.value)
          } catch {
            ctx.addIssue({
              code: 'custom',
              message: t('community-essentials:essentialFormModal.value.validation.invalidUrl'),
              path: ['value'],
            })
          }
        }
        if (data.type === 'email') {
          if (!EMAIL_REGEX.test(data.value)) {
            ctx.addIssue({
              code: 'custom',
              message: t('community-essentials:essentialFormModal.value.validation.invalidEmail'),
              path: ['value'],
            })
          }
        }
      }
    })

export type EssentialFormData = z.infer<ReturnType<typeof makeEssentialFormSchema>>
